/**
 * intakeFormState.ts — pure state helpers for the public intake wizard.
 * No React, no fetch: draft manipulation, client-side validation (mirrors the
 * server-authoritative rules in backend intakeService), and the workbook's
 * advisory hints. Unit-tested in intakeFormState.test.ts.
 */
import type {
  IntakeFormDef,
  IntakeTestParams,
  PublicIntakeDraft,
  PublicIntakeSample,
} from '../../../types/intake';

export const EMPTY_SAMPLE: PublicIntakeSample = {
  locationId: null,
  topDepth: null,
  baseDepth: null,
  sampleRef: null,
  sampleType: null,
  tests: [],
};

export function emptyDraft(): PublicIntakeDraft {
  return {
    projectId: null,
    projectName: null,
    projectLocation: null,
    clientName: null,
    contactName: null,
    contactEmail: null,
    dueDate: null,
    checklist: {},
    clientNotes: null,
    samples: [{ ...EMPTY_SAMPLE }],
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Quantity for a test on a sample (0 = not selected). */
export function testQuantity(sample: PublicIntakeSample, testKey: string): number {
  const t = sample.tests.find((x) => x.testKey.toUpperCase() === testKey.toUpperCase());
  return t ? t.quantity : 0;
}

/** Set a test's quantity on a sample (0 removes it). Returns a new sample. */
export function withTestQuantity(
  sample: PublicIntakeSample,
  testKey: string,
  quantity: number,
): PublicIntakeSample {
  const rest = sample.tests.filter((x) => x.testKey.toUpperCase() !== testKey.toUpperCase());
  if (quantity <= 0) return { ...sample, tests: rest };
  const existing = sample.tests.find((x) => x.testKey.toUpperCase() === testKey.toUpperCase());
  return {
    ...sample,
    tests: [...rest, { testKey, quantity: Math.floor(quantity), params: existing?.params ?? null }],
  };
}

/** Set the DS stresses on a sample's test row. Returns a new sample. */
export function withStresses(
  sample: PublicIntakeSample,
  testKey: string,
  stressesKpa: number[],
): PublicIntakeSample {
  return {
    ...sample,
    tests: sample.tests.map((t) =>
      t.testKey.toUpperCase() === testKey.toUpperCase()
        ? { ...t, params: stressesKpa.length > 0 ? { ...(t.params ?? {}), stressesKpa } : t.params }
        : t,
    ),
  };
}

/**
 * Set (or clear with null) the rock Triaxial confining pressure on a sample's
 * test row (BUG-2026-0182). Returns a new sample.
 */
export function withConfiningPressure(
  sample: PublicIntakeSample,
  testKey: string,
  confiningPressureMPa: number | null,
): PublicIntakeSample {
  return {
    ...sample,
    tests: sample.tests.map((t) => {
      if (t.testKey.toUpperCase() !== testKey.toUpperCase()) return t;
      const { confiningPressureMPa: _dropped, ...rest } = t.params ?? {};
      const params: IntakeTestParams = confiningPressureMPa == null ? rest : { ...rest, confiningPressureMPa };
      return { ...t, params: Object.keys(params).length > 0 ? params : null };
    }),
  };
}

/**
 * Chip/label suffix for a test row's parameters — " @ 100/250 kPa" for DS
 * stresses, " · σ₃ 4 MPa" for triaxial confining pressure. One formatter for
 * the review step, the lab's sample matrix, and the import preview.
 */
export function testParamsSuffix(params: IntakeTestParams | null | undefined): string {
  const parts: string[] = [];
  if (params?.stressesKpa?.length) parts.push(`@ ${params.stressesKpa.join('/')} kPa`);
  if (params?.confiningPressureMPa != null) parts.push(`σ₃ ${params.confiningPressureMPa} MPa`);
  return parts.length > 0 ? ` ${parts.join(' · ')}` : '';
}

/** Fields the project-info step can flag for highlight/flash. */
export type ProjectInfoField = 'projectName' | 'clientName' | 'contactName' | 'contactEmail';
/** Per-sample fields the grid step can flag for highlight/flash. */
export type SampleField = 'locationId' | 'topDepth' | 'baseDepth' | 'tests';

export interface StepValidation {
  /** Errors keyed for display; empty = the step is complete. */
  errors: string[];
  warnings: string[];
  /** Project-info fields in error (highlight + flash targets). */
  fields?: ProjectInfoField[];
  /** Sample-row fields in error, keyed by row index. */
  rows?: Record<number, SampleField[]>;
}

export function validateChecklist(draft: PublicIntakeDraft, def: IntakeFormDef): StepValidation {
  const errors = def.checklistItems
    .filter((item) => !draft.checklist?.[item.id])
    .map((item) => `Please confirm: ${item.text}`);
  return { errors, warnings: [] };
}

export function validateProjectInfo(draft: PublicIntakeDraft): StepValidation {
  const errors: string[] = [];
  const fields: ProjectInfoField[] = [];
  if (!draft.projectName?.trim()) { errors.push('Project Name is required.'); fields.push('projectName'); }
  if (!draft.clientName?.trim()) { errors.push('Client Name is required.'); fields.push('clientName'); }
  if (!draft.contactName?.trim()) { errors.push('Contact Person is required.'); fields.push('contactName'); }
  const email = draft.contactEmail?.trim() ?? '';
  if (!email) { errors.push('Contact Email is required.'); fields.push('contactEmail'); }
  else if (!EMAIL_RE.test(email)) { errors.push('Contact Email is not a valid email address.'); fields.push('contactEmail'); }
  return { errors, warnings: [], fields };
}

export function validateSamples(draft: PublicIntakeDraft, def: IntakeFormDef): StepValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const rows: Record<number, SampleField[]> = {};
  const flag = (i: number, f: SampleField) => {
    rows[i] = rows[i] ?? [];
    if (!rows[i].includes(f)) rows[i].push(f);
  };
  const samples = draft.samples ?? [];
  if (samples.length === 0) errors.push('Add at least one sample row.');

  const paramKinds = new Map<string, string | null>();
  for (const cat of def.categories) {
    for (const t of cat.tests) paramKinds.set(t.key.toUpperCase(), t.paramKind);
  }

  let anyTest = false;
  samples.forEach((s, i) => {
    const row = `Sample ${i + 1}${s.locationId ? ` (${s.locationId})` : ''}`;
    if (!s.locationId?.trim()) { errors.push(`${row}: Location ID is required (e.g. BH-BGC21-01).`); flag(i, 'locationId'); }
    if (s.topDepth == null || !Number.isFinite(Number(s.topDepth))) {
      errors.push(`${row}: Top depth is required (metres, two decimal places).`);
      flag(i, 'topDepth');
    } else if (Number(s.topDepth) < 0) {
      errors.push(`${row}: Top depth cannot be negative.`);
      flag(i, 'topDepth');
    }
    if (
      s.baseDepth != null && Number.isFinite(Number(s.baseDepth)) &&
      s.topDepth != null && Number.isFinite(Number(s.topDepth)) &&
      Number(s.baseDepth) <= Number(s.topDepth)
    ) {
      errors.push(`${row}: Base depth must be below the top depth.`);
      flag(i, 'baseDepth');
    }
    if (s.tests.length === 0) warnings.push(`${row}: no tests selected — the sample will be checked in without test requests (tests can be added later).`);
    for (const t of s.tests) {
      anyTest = true;
      if (!Number.isInteger(t.quantity) || t.quantity < 1) {
        errors.push(`${row}: test quantity must be a whole number of 1 or more.`);
        flag(i, 'tests');
      }
      if (paramKinds.get(t.testKey.toUpperCase()) === 'stressesKpa') {
        // 2026-08-06 (Vahid): stresses are OPTIONAL at intake — validate only
        // values actually entered (max 5, positive); empty is fine, the lab
        // confirms stresses later. Mirrors intakeService.submitPublic.
        const stresses = t.params?.stressesKpa;
        if (Array.isArray(stresses) && stresses.length > 0) {
          if (stresses.length > 5) {
            errors.push(`${row}: rock Direct Shear takes at most 5 normal stresses (kPa).`);
            flag(i, 'tests');
          } else if (stresses.some((x) => !Number.isFinite(Number(x)) || Number(x) <= 0)) {
            errors.push(`${row}: Direct Shear stresses must be positive numbers (kPa).`);
            flag(i, 'tests');
          }
        } else {
          warnings.push(`${row}: no normal stresses given for rock Direct Shear — the lab will confirm them with you.`);
        }
      }
      if (paramKinds.get(t.testKey.toUpperCase()) === 'confiningPressureMPa') {
        // BUG-2026-0182: optional — only an entered value is checked. Mirrors
        // backend intakeFormService.validateTestParams.
        const cp = t.params?.confiningPressureMPa;
        if (cp != null && (!Number.isFinite(Number(cp)) || Number(cp) <= 0)) {
          errors.push(`${row}: Rock Triaxial confining pressure must be a positive number (MPa).`);
          flag(i, 'tests');
        }
      }
    }
  });
  // Sample-only submissions are allowed: field crews check samples in before
  // the project decides which tests to run.
  if (samples.length > 0 && !anyTest) {
    warnings.push('No tests selected — the samples will be checked in without test requests.');
  }
  return { errors, warnings, rows };
}

export function validateAll(draft: PublicIntakeDraft, def: IntakeFormDef): StepValidation {
  const a = validateChecklist(draft, def);
  const b = validateProjectInfo(draft);
  const c = validateSamples(draft, def);
  return {
    errors: [...a.errors, ...b.errors, ...c.errors],
    warnings: [...a.warnings, ...b.warnings, ...c.warnings],
  };
}

/**
 * Contextual hints (workbook Instructions sheet) derived from what's actually
 * selected — shown above the grid so clients pick the right combination.
 */
export function deriveActiveHints(draft: PublicIntakeDraft, def: IntakeFormDef): string[] {
  const nameByKey = new Map<string, string>();
  for (const cat of def.categories) {
    for (const t of cat.tests) nameByKey.set(t.key.toUpperCase(), t.name.toLowerCase());
  }
  const selectedNames = new Set<string>();
  for (const s of draft.samples ?? []) {
    for (const t of s.tests) {
      const n = nameByKey.get(t.testKey.toUpperCase());
      if (n) selectedNames.add(n);
    }
  }
  const has = (re: RegExp) => [...selectedNames].some((n) => re.test(n));

  const hints: string[] = [];
  const hasHydrometer = has(/hydrometer/);
  const hasSg = has(/specific gravity/);
  if (hasHydrometer && !hasSg) {
    hints.push('A hydrometer requires a value for SG — add a Specific Gravity test or provide a known SG value in the comments.');
  }
  if (has(/sieve/) && !hasHydrometer) {
    hints.push('Sieve only covers material above the No. 200 sieve — select "sieve and hydrometer" if you need the full particle size distribution.');
  }
  if (hasHydrometer && has(/fines/)) {
    hints.push('If the sample is >90% passing the No. 200 sieve, a hydrometer OR a fines content is usually enough — you may not need both.');
  }
  if (has(/direct shear/)) {
    hints.push('Rock Direct Shear: enter quantity 1 and list up to 5 normal stresses (kPa) on that row.');
  }
  if (has(/triaxial/) && has(/rock/)) {
    hints.push('Rock Triaxial: you may enter the confining pressure (MPa) under the quantity — optional, the lab will confirm it with you.');
  }
  return hints;
}
